import {
    Alert,
    Button,
    Flex,
    Form,
    Input,
    Modal,
    QRCode,
    Result,
    Spin,
    Steps,
    Typography,
    message,
} from 'antd';
import { QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import useAuthHeader from '../../../hooks/useAuthHeader';
import useBankAccountAuthFlow from '../../../hooks/useBankAccountAuthFlow';
import styles from './BankAccountAuthModal.module.scss';

const { Text, Paragraph } = Typography;

function BankAccountAuthModal({ open, onClose, onSuccess, bankOptions = [] }) {
    const accessToken = useAuthHeader();
    const [form] = Form.useForm();
    const [selectedBank, setSelectedBank] = useState('');
    const {
        step,
        bankName,
        bankIcon,
        authMethod,
        authNote,
        username,
        setUsername,
        otpMode,
        qrCode,
        authMessage,
        loading,
        error,
        pushWaiting,
        pushSecondsLeft,
        selectBank,
        goBackToBank,
        submitAuth,
        beginPushFlow,
        pollPushConfirm,
    } = useBankAccountAuthFlow({
        accessToken,
        open,
        onSuccess: () => {
            message.success('Đăng nhập tài khoản thành công');
            onSuccess?.();
        },
    });

    useEffect(() => {
        if (!open) {
            form.resetFields();
            setSelectedBank('');
        }
    }, [open, form]);

    useEffect(() => {
        if (authMethod === 'LOGIN_WITH_OTP' && otpMode === 'push' && open) {
            beginPushFlow();
        }
    }, [authMethod, otpMode, open, beginPushFlow]);

    const handleSelectBankContinue = async () => {
        if (!selectedBank) {
            message.error('Vui lòng chọn ngân hàng');
            return;
        }
        const option = bankOptions.find((item) => item.value === selectedBank);
        await selectBank(selectedBank, option?.icon || '');
    };

    const handleAuthSubmit = async () => {
        if (authMethod === 'CHECK_USERNAME') {
            const value = form.getFieldValue('username');
            if (!value?.trim()) {
                message.error('Vui lòng nhập tên tài khoản');
                return;
            }
            setUsername(value.trim());
            const { done } = await submitAuth({ cmd: 'CHECK_USERNAME', username: value.trim() });
            if (!done) {
                form.resetFields(['password', 'otp']);
            }
            return;
        }

        if (authMethod === 'LOGIN_WITH_PASSWORD') {
            const password = form.getFieldValue('password');
            if (!password) {
                message.error('Vui lòng nhập mật khẩu');
                return;
            }
            const { done, continuePush } = await submitAuth({ cmd: 'LOGIN_WITH_PASSWORD', password });
            if (!done && continuePush) {
                form.resetFields(['otp']);
            }
            return;
        }

        if (authMethod === 'LOGIN_WITH_OTP') {
            if (otpMode === 'push') {
                await pollPushConfirm(false);
                return;
            }
            const otp = form.getFieldValue('otp');
            if (!otp?.trim()) {
                message.error('Vui lòng nhập mã OTP');
                return;
            }
            await submitAuth({ cmd: 'LOGIN_WITH_OTP', otp: otp.trim() });
        }
    };

    const renderAuthForm = () => {
        const isPush = authMethod === 'LOGIN_WITH_OTP' && otpMode === 'push';

        return (
            <div>
                <div className={styles.authHeader}>
                    {bankIcon ? (
                        <img src={bankIcon} alt={bankName} className={styles.authBankLogo} />
                    ) : null}
                    <Typography.Title level={5} className={styles.authTitle}>
                        {bankName}
                    </Typography.Title>
                    {username ? <Text type="secondary">{username}</Text> : null}
                </div>

                {authNote ? (
                    <Alert type="info" showIcon message={authNote} style={{ marginBottom: 16 }} />
                ) : null}

                {error ? (
                    <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
                ) : null}

                {authMessage && !error ? (
                    <Alert type="info" showIcon message={authMessage} style={{ marginBottom: 16 }} />
                ) : null}

                <Form form={form} layout="vertical">
                    {authMethod === 'CHECK_USERNAME' && (
                        <Form.Item label="Tên đăng nhập" name="username" rules={[{ required: true, message: 'Vui lòng nhập tên tài khoản' }]}>
                            <Input placeholder="Nhập tên đăng nhập" autoComplete="username" />
                        </Form.Item>
                    )}

                    {authMethod === 'LOGIN_WITH_PASSWORD' && (
                        <Form.Item label="Mật khẩu" name="password" rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}>
                            <Input.Password placeholder="Nhập mật khẩu" autoComplete="current-password" />
                        </Form.Item>
                    )}

                    {authMethod === 'LOGIN_WITH_OTP' && !isPush && (
                        <Form.Item label="Mã OTP" name="otp" rules={[{ required: true, message: 'Vui lòng nhập mã OTP' }]}>
                            <Input placeholder="Nhập mã OTP SMS" autoComplete="one-time-code" inputMode="numeric" />
                        </Form.Item>
                    )}
                </Form>

                {isPush && (
                    <div className={styles.pushPanel}>
                        <div className={styles.pushBadge}>
                            <QrcodeOutlined />
                            Quét mã QR trên app
                        </div>
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            Mở app ngân hàng, quét mã QR và xác nhận đăng nhập.
                        </Paragraph>
                        {qrCode ? (
                            <div className={styles.qrWrap}>
                                <QRCode value={qrCode} size={224} bordered={false} />
                            </div>
                        ) : (
                            <Flex justify="center" style={{ padding: '24px 0' }}>
                                <Spin />
                            </Flex>
                        )}
                        <div className={styles.waitingRow}>
                            {pushWaiting ? <Spin size="small" /> : null}
                            <span>
                                {pushWaiting
                                    ? `Đang chờ xác nhận${pushSecondsLeft > 0 ? ` (${pushSecondsLeft}s)` : ''}...`
                                    : 'Nhấn "Tôi đã xác nhận" sau khi quét QR'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderFooter = () => {
        if (step === 'success') {
            return [
                <Button key="close" type="primary" onClick={onClose}>
                    Đóng
                </Button>,
            ];
        }

        if (step === 'bank') {
            return [
                <Button key="cancel" onClick={onClose}>
                    Hủy
                </Button>,
                <Button key="continue" type="primary" loading={loading} onClick={handleSelectBankContinue}>
                    Tiếp tục
                </Button>,
            ];
        }

        const isPush = authMethod === 'LOGIN_WITH_OTP' && otpMode === 'push';

        return [
            <Button key="cancel" onClick={onClose}>
                Hủy
            </Button>,
            <Button key="back" onClick={goBackToBank}>
                Quay lại
            </Button>,
            <Button
                key="submit"
                type="primary"
                loading={loading}
                icon={isPush ? <ReloadOutlined /> : undefined}
                onClick={handleAuthSubmit}
            >
                {isPush ? 'Tôi đã xác nhận' : 'Tiếp tục'}
            </Button>,
        ];
    };

    const currentStepIndex = step === 'bank' ? 0 : step === 'auth' ? 1 : 2;

    return (
        <Modal
            className={styles.authModal}
            title="Đăng nhập tài khoản ngân hàng"
            open={open}
            onCancel={onClose}
            footer={<div className={styles.footerActions}>{renderFooter()}</div>}
            width={560}
            destroyOnClose
            maskClosable={!loading}
        >
            <Steps
                className={styles.steps}
                current={currentStepIndex}
                size="small"
                items={[
                    { title: 'Chọn ngân hàng' },
                    { title: 'Xác thực' },
                    { title: 'Hoàn tất' },
                ]}
            />

            {step === 'bank' && (
                <div className={styles.bankGrid}>
                    {bankOptions.map((option) => {
                        const selected = selectedBank === option.value;
                        return (
                            <div
                                key={option.value}
                                className={`${styles.bankCard} ${selected ? styles.bankCardSelected : ''}`}
                                onClick={() => setSelectedBank(option.value)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setSelectedBank(option.value);
                                    }
                                }}
                            >
                                {option.icon ? (
                                    <img src={option.icon} alt={option.label} className={styles.bankLogo} />
                                ) : (
                                    <div className={styles.bankLogo} />
                                )}
                                <span className={styles.bankName}>{option.label}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {step === 'auth' && renderAuthForm()}

            {step === 'success' && (
                <div className={styles.successPanel}>
                    <Result
                        status="success"
                        title="Đăng nhập thành công"
                        subTitle="Tài khoản ngân hàng đã được kết nối và sẵn sàng sử dụng."
                    />
                </div>
            )}
        </Modal>
    );
}

export default BankAccountAuthModal;
